// Instituciones (universidades + centros clínicos) para sugerencias del
// onboarding y normalización del texto libre. La meta: que "san sebastián",
// "U. San Sebastián" y "Universidad San Sebastián" queden como un solo valor,
// y que las grafías de un mismo lugar (ej. "Bupa" / "Clínica BUPA") se unifiquen.
//
// No es exhaustivo: el input sigue permitiendo texto libre. Lo que no calza con
// el catálogo se guarda tal cual (solo limpiando espacios), sin forzar.

// Universidades chilenas con carrera de Enfermería (las más frecuentes).
export const UNIVERSIDADES = [
  "Universidad de Chile",
  "Pontificia Universidad Católica de Chile",
  "Universidad de Santiago de Chile",
  "Universidad San Sebastián",
  "Universidad del Desarrollo",
  "Universidad Andrés Bello",
  "Universidad de Concepción",
  "Universidad de Valparaíso",
  "Universidad de La Frontera",
  "Universidad Austral de Chile",
  "Universidad de los Andes",
  "Universidad Mayor",
  "Universidad Autónoma de Chile",
  "Universidad Católica del Norte",
  "Universidad de Las Américas",
  "Universidad Santo Tomás",
  "Universidad Diego Portales",
  "Universidad Central de Chile",
  "Universidad Finis Terrae",
  "Universidad SEK",
  "Universidad del Bío-Bío",
  "Universidad de Talca",
  "Universidad Católica del Maule",
  "Universidad de Antofagasta",
  "Universidad de Tarapacá",
  "Universidad Arturo Prat",
  "Universidad de Magallanes",
  "Universidad Bernardo O'Higgins",
  "Universidad Católica de la Santísima Concepción",
  "Universidad Pedro de Valdivia",
  // Sumadas el 2026-10-12 al mirar lo que la gente escribió de verdad en su
  // perfil (panel "Quiénes"): eran grafías sueltas que no se unificaban.
  "Universidad de O'Higgins",
  "Universidad del Alba",
  "Universidad de Atacama",
  "Universidad de La Serena",
  "Universidad de Playa Ancha",
  "Universidad Católica de Temuco",
  "Universidad de Aysén",
];

// Centros clínicos / empleadores frecuentes (para enfermeros/as en ejercicio).
export const CENTROS = [
  "Clínica Bupa",
  "Clínica Las Condes",
  "Clínica Alemana",
  "Clínica Santa María",
  "Clínica Dávila",
  "Clínica Indisa",
  "Hospital de Urgencia Asistencia Pública",
  "Hospital Dr. Sótero del Río",
  "Hospital del Salvador",
  "Hospital Barros Luco",
  "Hospital San Juan de Dios",
  "Hospital Clínico Universidad de Chile",
];

export const INSTITUCIONES = [...UNIVERSIDADES, ...CENTROS];

// Alias: grafías comunes / siglas → nombre canónico. Clave en minúsculas y sin
// acentos (ver clave()).
const ALIAS = {
  "bupa": "Clínica Bupa",
  "clinica bupa": "Clínica Bupa",
  "puc": "Pontificia Universidad Católica de Chile",
  "uc": "Pontificia Universidad Católica de Chile",
  "usach": "Universidad de Santiago de Chile",
  "uss": "Universidad San Sebastián",
  "udd": "Universidad del Desarrollo",
  "unab": "Universidad Andrés Bello",
  "udec": "Universidad de Concepción",
  "uchile": "Universidad de Chile",
  "u de chile": "Universidad de Chile",
  "uautonoma": "Universidad Autónoma de Chile",
  "udla": "Universidad de Las Américas",
  "ust": "Universidad Santo Tomás",
  "udp": "Universidad Diego Portales",
  "ubo": "Universidad Bernardo O'Higgins",
  "huap": "Hospital de Urgencia Asistencia Pública",
  // Siglas y grafías vistas en producción (2026-10-12).
  "ufro": "Universidad de La Frontera",
  "u de la frontera": "Universidad de La Frontera",
  "uoh": "Universidad de O'Higgins",
  "universidad de ohiggins": "Universidad de O'Higgins",
  "universidad o'higgins": "Universidad de O'Higgins",
  "universidad ohiggins": "Universidad de O'Higgins",
  "sek": "Universidad SEK",
  "usek": "Universidad SEK",
  "uisek": "Universidad SEK",
  "utalca": "Universidad de Talca",
  "utal": "Universidad de Talca",
  "u de talca": "Universidad de Talca",
  "uta": "Universidad de Tarapacá",
  "ucn": "Universidad Católica del Norte",
  "uv": "Universidad de Valparaíso",
  "uda": "Universidad de Atacama",
  "upla": "Universidad de Playa Ancha",
  "playa ancha": "Universidad de Playa Ancha",
  "santo tomas": "Universidad Santo Tomás",
  "umayor": "Universidad Mayor",
  "u mayor": "Universidad Mayor",
  "uft": "Universidad Finis Terrae",
  "finis terrae": "Universidad Finis Terrae",
  "uach": "Universidad Austral de Chile",
  "uandes": "Universidad de los Andes",
  "ucm": "Universidad Católica del Maule",
  "ubb": "Universidad del Bío-Bío",
  "universidad autonoma": "Universidad Autónoma de Chile",
  "uct": "Universidad Católica de Temuco",
  "userena": "Universidad de La Serena",
  "udla la florida": "Universidad de Las Américas",
  "bupa santiago": "Clínica Bupa",
  "clinica bupa santiago": "Clínica Bupa",
  "sotero del rio": "Hospital Dr. Sótero del Río",
  "hospital sotero del rio": "Hospital Dr. Sótero del Río",
  "indisa": "Clínica Indisa",
  "santa maria": "Clínica Santa María",
};

function clave(s) {
  return String(s || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "") // sin acentos
    .replace(/[’‘`´]/g, "'") // el apóstrofo curvo del teclado del teléfono es el mismo apóstrofo
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

// Índice canónico por clave normalizada (para match exacto sin acentos/casing).
const POR_CLAVE = new Map(INSTITUCIONES.map((i) => [clave(i), i]));

/**
 * Normaliza una institución escrita a mano: unifica alias y casing/acentos
 * contra el catálogo. Si no calza, devuelve el texto limpio (espacios) tal cual.
 */
export function normalizarInstitucion(raw) {
  const s = String(raw || "").replace(/\s+/g, " ").trim();
  if (!s) return "";
  const k = clave(s);
  if (ALIAS[k]) return ALIAS[k];
  if (POR_CLAVE.has(k)) return POR_CLAVE.get(k);
  return s;
}

/**
 * ¿La institución escrita es Bupa? El campo es texto libre con autocompletar,
 * y en producción (2026-10-06) había enfermeras y TENS de Bupa con "BUPA
 * Santiago", "Bupa UCI" o "En clínica bupa UPC coronaria": el alias exacto los
 * dejaba fuera de la rutina del piloto sin aviso. Cuenta cualquier texto que
 * nombre "bupa" como palabra (sin acentos ni mayúsculas).
 */
export function esInstitucionBupa(raw) {
  return /(^|[^a-z0-9])bupa([^a-z0-9]|$)/.test(clave(raw));
}

// Sugerencias para el autocomplete (coincidencia por substring, sin acentos).
export function sugerirInstituciones(valor, max = 6) {
  const k = clave(valor);
  if (!k) return [];
  return INSTITUCIONES.filter((i) => clave(i).includes(k)).slice(0, max);
}
